import React, { useCallback, useEffect, useState } from 'react';
import { Button, Descriptions, Form, Modal, Skeleton } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import CBInputNumber from '../../components/CBInputNumber';
import axiosInstance from '../../variables/api/axiosInstance';
import { can, formatMontant } from '../../variables/constants';
import { HAB } from '../../variables/habilitations';
import { GREEN } from '../comptabilite/commun';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { localeIntl } from '../../i18n/format';

/* LE TARIF PROPOSE A LA CREATION D'UN EXPERT.

   Il valait 40 000 en dur, a deux endroits qui ne se parlaient pas - l'initialiseur
   du champ cote Java et le DEFAULT de la colonne cote PostgreSQL - et aucun des deux
   ne servait : le formulaire n'envoyant pas le champ, le serveur posait NULL dans une
   colonne qui le refuse, et creer un expert depuis le web echouait.

   Le montant est desormais le parametre MONT_EXPERTISE_DEFAUT. Il ne vaut qu'a la
   CREATION : un expert deja enregistre garde le tarif qu'il porte, et le modifier ici
   ne reecrit aucune fiche. C'est dit a l'ecran, parce que c'est la question que pose
   tout reglage de ce genre. */

/** Borne de la colonne `parametre_systeme.valeur_decimal`, NUMERIC(10,4). */
const MONTANT_MAX = 999999;

export default function MontantDefaut() {
  const { t } = useTranslation('experts');
  const [montant, setMontant] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [ouvert, setOuvert] = useState(false);
  const [envoi, setEnvoi] = useState(false);
  const [form] = Form.useForm();
  const modifiable = can(HAB.EXPERT_UPDATE);

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      const r = await axiosInstance.get('/v1/experts/montant-defaut');
      setMontant(r.data?.data?.montExpertise ?? null);
    } catch {
      // Le serveur se replie sur la valeur historique si le parametre manque : un
      // echec ici est un probleme de reseau, pas un parametre absent.
      toast.error(i18n.t('experts:montantDefaut.erreurLecture'));
    } finally {
      setChargement(false);
    }
  }, []);

  useEffect(() => { charger(); }, [charger]);

  const ouvrir = () => {
    form.setFieldsValue({ montExpertise: montant != null ? Number(montant) : undefined });
    setOuvert(true);
  };

  const enregistrer = async ({ montExpertise }) => {
    setEnvoi(true);
    try {
      const r = await axiosInstance.put('/v1/experts/montant-defaut', { montExpertise });
      setMontant(r.data?.data?.montExpertise ?? montExpertise);
      toast.success(t('montantDefaut.enregistre'));
      setOuvert(false);
    } catch (e) {
      toast.error(e?.response?.data?.message || t('montantDefaut.erreurEnregistrement'));
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        gap: 12, marginBottom: 14, flexWrap: 'wrap',
      }}>
        <span style={{ color: '#667085', fontSize: 13 }}>
          {t('montantDefaut.aide')}
        </span>
        {modifiable && (
          <Button type="primary" icon={<EditOutlined />} onClick={ouvrir} disabled={chargement}
            style={{ background: GREEN, borderColor: GREEN, borderRadius: 8 }}>
            {t('montantDefaut.bouton')}
          </Button>
        )}
      </div>

      {chargement ? (
        <Skeleton active paragraph={{ rows: 1 }} title={false} />
      ) : (
        <Descriptions size="small" column={1} bordered>
          <Descriptions.Item label={t('montantDefaut.titre')}>
            <span style={{ fontWeight: 700, fontSize: 15, fontVariantNumeric: 'tabular-nums' }}>
              {montant != null ? formatMontant(montant) : '—'}
            </span>
          </Descriptions.Item>
        </Descriptions>
      )}

      <Modal title={t('montantDefaut.titre')} open={ouvert} footer={null}
        onCancel={() => setOuvert(false)} width={460} destroyOnClose>
        <Form form={form} layout="vertical" onFinish={enregistrer} style={{ marginTop: 16 }}>
          <Form.Item
            name="montExpertise"
            label={t('montantDefaut.montant')}
            rules={[
              { required: true, message: t('montantDefaut.montantRequis') },
              /* LA BORNE DE LA COLONNE, DITE ICI. `valeur_decimal` est NUMERIC(10,4) :
                 au-dela, PostgreSQL refuse l'ecriture. Le serveur le refuse aussi, avec
                 un message clair ; le dire a la saisie evite l'aller-retour. */
              {
                type: 'number', min: 0, max: MONTANT_MAX,
                message: t('montantDefaut.borne', { max: MONTANT_MAX.toLocaleString(localeIntl()) }),
              },
            ]}
            extra={t('montantDefaut.extra')}
          >
            <CBInputNumber min={0} max={MONTANT_MAX} step={5000} precision={0}
              addonAfter="F CFA" style={{ width: '100%' }} />
          </Form.Item>
          <div style={{
            display: 'flex', gap: 8, justifyContent: 'flex-end',
            borderTop: '1px solid #e5e7eb', paddingTop: 16,
          }}>
            <Button onClick={() => setOuvert(false)}>{t('montantDefaut.annuler')}</Button>
            <Button type="primary" htmlType="submit" loading={envoi}
              style={{ background: GREEN, borderColor: GREEN, borderRadius: 8 }}>
              {t('montantDefaut.enregistrer')}
            </Button>
          </div>
        </Form>
      </Modal>
    </>
  );
}
